import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ParkingImage from '../assets/nimbleparknewlogo.svg';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../config';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { IconButton } from '@mui/material';


const useStyles = makeStyles(() => ({
  rootContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    padding: '16px',
    overflow: 'hidden',
    fontFamily: 'Open Sans, Nunito, sans-serif',
  },
  forgotPasswordCard: {
    width: '100%',
    position: 'relative',
    background: 'white',
    overflow: 'hidden',
    borderRadius: 30,
    minHeight: '50rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    '@media (max-width: 768px)': {
      borderRadius: 0,
      boxShadow: 'none',
      minHeight: '100vh',
      padding: '20px',
    },
  },
  title: {
    color: '#C084FC',
    fontSize: 18,
    fontFamily: 'Open Sans',
    fontWeight: '600',
    lineHeight: '19.10px',
    wordWrap: 'break-word',
    textAlign: 'center',
    marginBottom: '8px',
  },
  subtitle: {
    color: '#989898',
    fontSize: 14,
    fontFamily: 'Open Sans',
    fontWeight: '400',
    lineHeight: '19.10px',
    wordWrap: 'break-word',
    textAlign: 'center',
    marginBottom: '32px',
  },
  inputLabel: {
    color: '#C084FC',
    fontSize: 16,
    fontFamily: 'Open Sans',
    fontWeight: '600',
    lineHeight: '19.10px',
    wordWrap: 'break-word',
    textAlign: 'left',
    width: '100%',
    marginBottom: '8px',
  },
  inputField: {
    width: '100%',
    height: 50,
    marginBottom: '24px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1px black solid',
      '& fieldset': {
        border: '1px black solid',
      },
      '&:hover fieldset': {
        border: '1px black solid',
      },
      '&.Mui-focused fieldset': {
        border: '1px black solid',
      },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 14,
      fontFamily: 'Open Sans',
      fontWeight: '400',
      color: '#B3B3B3',
      '&::placeholder': {
        color: '#B3B3B3',
        opacity: 1,
      },
    },
  },
  resetButton: {
    width: '100%',
    height: 51,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    borderRadius: 10.40,
    border: 'none',
    color: 'white',
    fontSize: 18,
    fontFamily: 'Nunito',
    fontWeight: '700',
    lineHeight: '33.19px',
    cursor: 'pointer',
    marginTop: '16px',
    '&:hover': {
      background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
    },
    '&:disabled': {
      background: '#E0E0E0',
      color: '#9E9E9E',
    },
  },
  backLink: {
    position: 'absolute',
    bottom: 10,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#C084FC',
    textDecoration: 'none',
    fontSize: 14,
    fontFamily: 'Open Sans',
    fontWeight: '500',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Open Sans',
  },
  successText: {
    color: '#4caf50',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Open Sans',
  },
  loadingSpinner: {
    color: 'white',
  },
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const [contactInfo, setContactInfo] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const isEmail = (input) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input);
  };

  const isPhone = (input) => {
    const digits = input.replace(/\D/g, '');
    return digits.length === 10;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isEmail(contactInfo) && !isPhone(contactInfo)) {
      setError('Please enter a valid email or phone number');
      return;
    }

    setIsLoading(true);

    try {
      // Generate OTP
      const generatePayload = isEmail(contactInfo) 
        ? { email: contactInfo }
        : { phone: contactInfo };

      const generateResponse = await axios.post(`${config.API_BASE_URL}/forgot-password`, generatePayload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (generateResponse.data.message) {
        setSuccess('OTP sent successfully');
        // Navigate to reset password page with contact info
        navigate('/reset-password', { 
          state: { 
            contactInfo,
            type: isEmail(contactInfo) ? 'email' : 'phone'
          } 
        });
      }
    } catch (error) {
      console.error('Error generating OTP:', error);
      setError(error.response?.data?.error || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.rootContainer}>
      <div className={classes.forgotPasswordCard}>
          {/* Back Button */}
  <IconButton
    onClick={() => navigate('/login')}
    style={{
      position: 'absolute',
      top: 'clamp(12px, 2vw, 20px)',
      left: 'clamp(12px, 2vw, 20px)',
      background: '#F5F5F5',
      borderRadius: '50%',
      padding: 'clamp(4px, 1vw, 8px)',
    }}
  >
    <ArrowBackIosNewIcon style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#C084FC' }} />
  </IconButton>
        {/* Title */}
        <div className={classes.title}>
          Forgot password
        </div>

        {/* Subtitle */}
        <div className={classes.subtitle}>
          Please enter your registered login id.
        </div>

        {/* Input Label */}
        <div className={classes.inputLabel}>
          Your Email/Mobile No.
        </div>

        {/* Input Field */}
        <TextField
          className={classes.inputField}
          fullWidth
          placeholder="Enter your email/mobile no."
          variant="outlined"
          value={contactInfo}
          onChange={(e) => setContactInfo(e.target.value)}
          disabled={isLoading}
        />

        {/* Reset Button */}
        <button
          className={classes.resetButton}
          onClick={handleResetPassword}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} className={classes.loadingSpinner} />
          ) : (
            'Reset Password'
          )}
        </button>

        {/* Error Message */}
        {error && (
          <div style={{ 
            position: 'absolute', 
            bottom: 100, 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: '80%',
            textAlign: 'center'
          }}>
            <Typography className={classes.errorText}>
              {error}
            </Typography>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div style={{ 
            position: 'absolute', 
            bottom: 10, 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: '80%',
            textAlign: 'center'
          }}>
            <Typography className={classes.successText}>
              {success}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;